/**
 * @fileoverview A hook that provides user-related API functions.
 *
 * @example
 * const { result } = renderHook(() => useUserAPI())
 * expect(result.current.logIn).toBeDefined() // When logIn is available
 */

import { useLocalStorage } from './useLocalStorage'

/**
 * @interface UserAPI
 * @description Interface for user-related API functions.
 * @property {() => Novoic.API.User | undefined} getUser - Function to get the current user.
 * @property {() => string | undefined} getToken - Function to get the current token.
 * @property {() => boolean} isLoggedIn - Function to check if the user is logged in.
 * @property {(user: Novoic.API.User, intercom: Record<string, string>, token: string) => void} logIn - Function to log in a user.
 * @property {() => void} logOut - Function to log out a user.
 */
interface UserAPI {
    getUser: () => Novoic.API.User | undefined
    getToken: () => string | undefined
    isLoggedIn: () => boolean
    logIn: (user: Novoic.API.User, intercom: Record<string, string>, token: string) => void
    logOut: () => void
}

const USER = 'user'
const TOKEN = 'token'
const INTERCOM = 'intercom'

/**
 * @function useUserAPI
 * @description Hook to manage user-related API functions.
 * @returns {UserAPI} - An object containing user-related API functions.
 */
export function useUserAPI(): UserAPI {
    const [persist, retrieve, erase] = useLocalStorage()

    function logIn(user: Novoic.API.User, intercom: Record<string, string>, token: string): void {
        persist(USER, user)
        persist(TOKEN, token)
        persist(INTERCOM, intercom)
    }

    function logOut() {
        erase(USER)
        erase(TOKEN)
        erase(INTERCOM)
    }

    function getUser(): Novoic.API.User | undefined {
        const user = retrieve<Novoic.API.User>(USER)

        if (user) {
            return user
        }

        return undefined
    }

    function getToken(): string | undefined {
        const token = retrieve<string>(TOKEN)

        if (token) {
            return token
        }

        return undefined
    }

    function isLoggedIn(): boolean {
        const user = getUser()
        return user != null
    }

    return {
        getUser,
        getToken,
        isLoggedIn,
        logIn,
        logOut,
    }
}
